import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { SectionHeading } from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/content";
import { TrendingUp, ShieldCheck } from "lucide-react";
import { CountUp } from "@/components/visuals/AnimatedVisuals";

/* Compact animated visuals used as case study headers */
const LineVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <div ref={ref} className="relative h-44 bg-[#0d0e14] p-4">
      <svg viewBox="0 0 400 130" className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="csLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2AA7A1" />
            <stop offset="100%" stopColor="#D6B36A" />
          </linearGradient>
        </defs>
        {[32, 64, 96].map((y) => (
          <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#23253a" strokeWidth="1" />
        ))}
        <motion.path
          d="M8,118 C60,112 90,100 130,88 C170,76 200,68 240,52 C280,36 330,26 392,12"
          fill="none"
          stroke="url(#csLine)"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <motion.span
        initial={{ opacity: 0, scale: 0.7 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ delay: 1.3, duration: 0.5 }}
        className="absolute top-4 right-4 text-xs font-semibold bg-primary/15 text-primary border border-primary/30 rounded-full px-3 py-1"
      >
        +64% revenue
      </motion.span>
    </div>
  );
};

const DashboardVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const bars = [55, 78, 42, 88, 66, 92];
  return (
    <div ref={ref} className="h-44 bg-[#0d0e14] p-4 flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-secondary/60 border border-border px-3 py-2">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Items Tracked</p>
          <p className="text-sm font-semibold text-foreground">
            <CountUp to={12480} duration={1.4} />
          </p>
        </div>
        <div className="rounded-lg bg-secondary/60 border border-border px-3 py-2">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Error Reduction</p>
          <p className="text-sm font-semibold text-primary">
            -<CountUp to={40} duration={1.4} suffix="%" />
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-end gap-1.5">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-t bg-accent/60"
            initial={{ height: 0 }}
            animate={inView ? { height: `${h}%` } : {}}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
          />
        ))}
      </div>
    </div>
  );
};

const NetworkMiniVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const nodes = [
    { cx: 200, cy: 65, r: 13, main: true },
    { cx: 80, cy: 26, r: 7 },
    { cx: 320, cy: 30, r: 7 },
    { cx: 55, cy: 105, r: 7 },
    { cx: 340, cy: 100, r: 7 },
    { cx: 140, cy: 118, r: 7 },
    { cx: 265, cy: 120, r: 7 },
  ];
  return (
    <div ref={ref} className="h-44 bg-[#0d0e14] p-4">
      <svg viewBox="0 0 400 140" className="w-full h-full">
        {nodes.slice(1).map((n, i) => (
          <motion.line
            key={i}
            x1="200"
            y1="65"
            x2={n.cx}
            y2={n.cy}
            stroke="#2AA7A1"
            strokeWidth="1.2"
            strokeDasharray="4 4"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={inView ? { pathLength: 1, opacity: 0.55 } : {}}
            transition={{ duration: 0.7, delay: 0.12 * i }}
          />
        ))}
        {nodes.map((n, i) => (
          <motion.circle
            key={i}
            cx={n.cx}
            cy={n.cy}
            r={n.r}
            fill={n.main ? "#D6B36A" : "#171821"}
            stroke={n.main ? "#D6B36A" : "#2AA7A1"}
            strokeWidth="1.6"
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ duration: 0.45, delay: 0.09 * i }}
            style={{ transformOrigin: `${n.cx}px ${n.cy}px` }}
          />
        ))}
        <motion.circle
          cx="200"
          cy="65"
          r="20"
          fill="none"
          stroke="#D6B36A"
          strokeWidth="1.2"
          animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
          style={{ transformOrigin: "200px 65px" }}
        />
      </svg>
    </div>
  );
};

const ShieldMiniVisual = () => (
  <div className="h-44 bg-[#0d0e14] relative flex items-center justify-center">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="absolute rounded-full border border-primary/30"
        style={{ width: 60 + i * 45, height: 60 + i * 45 }}
        animate={{ scale: [1, 1.3], opacity: [0.5, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.7, ease: "easeOut" }}
      />
    ))}
    <div className="relative z-10 w-14 h-14 rounded-xl bg-primary/15 border border-primary/40 grid place-items-center">
      <ShieldCheck className="w-6 h-6 text-primary" />
    </div>
    <span className="absolute bottom-3 right-4 text-[10px] font-mono-meta text-emerald-400">
      0 breaches · 24/7 monitored
    </span>
  </div>
);

const VISUALS = {
  line: LineVisual,
  dashboard: DashboardVisual,
  network: NetworkMiniVisual,
  shield: ShieldMiniVisual,
};

export const CaseStudies = () => {
  return (
    <section id="work" className="py-16 sm:py-20 lg:py-24" data-testid="case-studies-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <SectionHeading
            eyebrow="Representative Work"
            title="Results our clients talk about"
            description="A snapshot of the kinds of projects we deliver — from e-commerce growth to bespoke business systems."
          />
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {CASE_STUDIES.map((study, i) => {
            const Visual = VISUALS[study.visual] || LineVisual;
            return (
              <motion.article
                key={study.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.65, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="card-highlight group rounded-2xl bg-card/80 border border-border overflow-hidden hover:border-primary/35 hover:shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-[border-color,box-shadow] duration-300"
                data-testid="case-study-card"
              >
                <div className="relative border-b border-border">
                  <Visual />
                  <Badge className="absolute top-4 left-4 bg-background/80 backdrop-blur text-primary border border-primary/30 rounded-full text-[11px]">
                    {study.category}
                  </Badge>
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-semibold tracking-tight text-foreground">
                    {study.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground font-editorial">
                    {study.summary}
                  </p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {study.metrics.map((metric) => (
                      <span
                        key={metric}
                        className="inline-flex items-center gap-1.5 text-xs font-mono-meta text-primary bg-primary/10 border border-primary/25 rounded-full px-3 py-1"
                      >
                        <TrendingUp className="w-3 h-3" /> {metric}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {study.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="border-border text-muted-foreground text-[11px] rounded-full">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-muted-foreground font-mono-meta">
          * Representative engagements. Client names withheld for confidentiality.
        </p>
      </div>
    </section>
  );
};
